// lib/intelligence/sample-trading-domain.js
// ═══════════════════════════════════════════════════════════
// SAMPLE DATA — Trading domain dashboard
// ═══════════════════════════════════════════════════════════
// Every number, ticker, lens excerpt, and pathway button on
// the Dashboard tab lives here. When Layer 2 (real APIs) and
// Layer 3 (Claude-generated lens text) come online they'll
// replace the values returned from this module with the same
// shapes — UI components don't change.
//
// Per the Layer 1/2/3 build principle: do not skip ahead. A
// section with real data and no lens commentary is better
// than a section with fake commentary.
// ═══════════════════════════════════════════════════════════

export const TRADING_DOMAIN = {
  meta: {
    domainName: 'Trading',
    asOf: 'Fri Apr 18 · 7:04 AM ET · Pre-Market',
    sessionDay: 14,
  },

  // Topbar market chips
  topbar: [
    { label: 'SPY', value: '506.44', change: '+0.34%', dir: 'up' },
    { label: 'QQQ', value: '437.82', change: '+0.41%', dir: 'up' },
    { label: 'IWM', value: '198.14', change: '−0.12%', dir: 'dn' },
    { label: 'VIX', value: '18.22', change: '+1.2%',  dir: 'dn' },
    { label: '10Y', value: '4.61%',  change: '+8bp',  dir: 'dn' },
    { label: 'DXY', value: '105.84', change: '−0.22%', dir: 'up' },
  ],

  // 1 — Market Regime (full-width, teal-top)
  regime: {
    eyebrow: 'Current Regime · Apr 18, 2026 · Day 14',
    title: 'Trending · ',
    titleAccent: 'Elevated Volatility',
    sub: '<strong>Breakout setups have follow-through.</strong> Size down 20-30% for elevated vol. Today\'s FOMC minutes at 2pm is the primary regime-shifting risk. If language is hawkish — trending regime weakens.',
    tags: [
      { kind: 'works', label: 'Momentum works' },
      { kind: 'works', label: 'Trend following rewarded' },
      { kind: 'caution', label: 'Size down for vol' },
      { kind: 'caution', label: 'Avoid premium selling' },
    ],
    chips: [
      { tone: 'teal',  label: 'Trend Direction',  value: 'Upward · Intact' },
      { tone: 'terra', label: 'Volatility Regime', value: 'Elevated · VIX 18+' },
      { tone: 'gold',  label: 'Breadth Health',    value: '68.4% above 200MA' },
      { tone: 'sage',  label: 'Liquidity',         value: 'Normal · Pre-FOMC' },
      { tone: 'teal',  label: 'Regime Duration',   value: 'Day 14 of current' },
    ],
    lens: {
      preview: 'What trending + elevated vol means for how you trade today…',
      eyebrow: 'Practitioner Framework — Trending Elevated Volatility',
      body: 'In a trending elevated volatility regime <strong>you want to be directional, not neutral.</strong> This is not the regime to sell premium or run iron condors — the vol is too high and the trend means directional moves can extend further than mean reversion traders expect. The risk: elevated vol creates violent counter-trend moves within the trend. <em>The trend is intact but the path is rough.</em> Position sizing is the most important decision you make today — not entry selection. Reduce sizes by 20-30% versus a low-vol trending regime.',
      pathways: ['understand', 'ask', 'apply'],
      pathwayLabels: { understand: 'Understand Market Regimes →', ask: 'Ask a question →', apply: 'Apply to Workstation →' },
    },
  },

  // 2a — US Futures + International (50/50 left)
  futures: {
    us: [
      { name: 'S&P 500', value: '5,064',  change: '+0.34%', dir: 'up' },
      { name: 'Nasdaq',  value: '17,842', change: '+0.41%', dir: 'up' },
      { name: 'Russell', value: '2,018',  change: '−0.12%', dir: 'dn' },
      { name: 'Dow',     value: '38,440', change: '+0.28%', dir: 'up' },
    ],
    intl: [
      { name: 'Nikkei',    change: '+0.82%', dir: 'up' },
      { name: 'Hang Seng', change: '−0.44%', dir: 'dn' },
      { name: 'DAX',       change: '+0.31%', dir: 'up' },
      { name: 'FTSE',      change: '+0.18%', dir: 'up' },
      { name: 'Shanghai',  change: '−0.22%', dir: 'dn' },
      { name: 'CAC 40',    change: '+0.24%', dir: 'up' },
    ],
    lens: {
      preview: 'Reading large-cap vs small-cap futures divergence…',
      eyebrow: 'Reading Pre-Market as a Practitioner',
      body: 'The divergence between Nasdaq (+0.41%) and Russell (−0.12%) is the signal. <strong>When large-cap leads and small-cap lags, risk-on is narrow — not broad.</strong> Broad participation requires small-caps to join. Watch breadth in the first 30 minutes to confirm whether the big-cap move has participation or is being carried by a few names.',
      pathways: ['understand', 'apply'],
      pathwayLabels: { understand: 'Understand Futures →', apply: 'Apply to Workstation →' },
    },
  },

  // 2b — Pre-Market Movers (50/50 right)
  movers: {
    up: [
      { ticker: 'NVDA', reason: 'Analyst upgrade, target raised to $1,100', change: '+3.2%' },
      { ticker: 'NFLX', reason: 'Subscriber beat in early data',            change: '+2.8%' },
      { ticker: 'AXP',  reason: 'Earnings beat — $3.97 vs $3.82 exp',      change: '+1.9%' },
      { ticker: 'XOM',  reason: 'WTI +1.24% on OPEC supply cut',           change: '+1.4%' },
    ],
    down: [
      { ticker: 'BABA', reason: 'China regulatory concern',          change: '−2.1%' },
      { ticker: 'INTC', reason: 'Guidance cut, margin pressure',     change: '−1.8%' },
      { ticker: 'MRNA', reason: 'Trial data disappointment',         change: '−1.4%' },
    ],
    lens: {
      preview: 'The gap is the setup, not the trade…',
      eyebrow: 'Reading Gap Moves as a Practitioner',
      body: 'Pre-market gaps on light volume are unreliable direction signals. <strong>The gap is the setup, not the trade.</strong> The trade is what happens in the first 15-30 minutes when real institutional volume enters. A gap that holds and builds volume = continuation. A gap that fades on volume = gap fill trade. Never chase the pre-market price.',
      pathways: ['understand', 'ask', 'apply'],
      pathwayLabels: { understand: 'Understand Gap Trading →', ask: 'Ask about gap fills →', apply: 'Apply to Workstation →' },
    },
  },

  // 3a — Event Calendar (50/50 left, gold-top)
  events: {
    today: [
      { time: '7:30 AM',  level: 'hi', name: 'Initial Jobless Claims',     meta: 'Exp: 215K · Prev: 222K · High impact' },
      { time: '8:30 AM',  level: 'md', name: 'Philly Fed Manufacturing',   meta: 'Exp: 1.5 · Prev: 3.2 · Medium' },
      { time: '10:00 AM', level: 'md', name: 'Existing Home Sales',         meta: 'Exp: 4.18M · Prev: 4.38M' },
      { time: '2:00 PM',  level: 'fomc', name: 'FOMC Minutes Release',      meta: 'High impact · Market-moving · IV spike expected' },
    ],
    week: [
      { time: 'Tue', level: 'md', name: 'CB Consumer Confidence' },
      { time: 'Wed', level: 'hi', name: 'Durable Goods + 5Y Auction' },
    ],
    lens: {
      preview: 'One event dominates today — how it changes position sizing…',
      eyebrow: 'Reading Event Risk as a Practitioner',
      body: 'Today has one genuine market-moving event: FOMC minutes at 2pm. <strong>Practical implication: the market will trade cautiously before 2pm and violently after.</strong> Morning entries need wider stops for low-volume choppiness. Post-2pm entries can be sized more aggressively once direction confirms — the volume will support the move.',
      pathways: ['understand', 'ask', 'apply'],
      pathwayLabels: { understand: 'Understand Event Risk →', ask: 'Ask about FOMC day trading →', apply: 'Apply to Workstation →' },
    },
  },

  // 3b — Earnings (50/50 right)
  earnings: {
    pre: [
      { ticker: 'PG',  name: 'Procter & Gamble',      eps: 'Exp: $1.41',         move: '±2.4% exp move' },
      { ticker: 'AXP', name: 'American Express',      eps: 'Beat: $3.97 vs $3.82', move: '+1.9% pre-mkt', moveTone: 'sage' },
    ],
    post: [
      { ticker: 'NFLX', name: 'Netflix',         eps: 'Exp: $5.68', move: '±6.8% exp move' },
      { ticker: 'COF',  name: 'Capital One',     eps: 'Exp: $3.91', move: '±3.2% exp move' },
    ],
    week: [
      { ticker: 'TSLA', name: 'Tuesday AC',  eps: 'Exp: $0.61', move: '±7.2%' },
      { ticker: 'META', name: 'Wednesday AC', eps: 'Exp: $4.68', move: '±5.1%' },
      { ticker: 'MSFT', name: 'Thursday AC',  eps: 'Exp: $3.22', move: '±4.3%' },
      { ticker: 'AMZN', name: 'Thursday AC',  eps: 'Exp: $1.04', move: '±5.8%' },
    ],
    season: [
      { label: 'Season beat rate', value: '78% · 14% reported', tone: 'sage' },
      { label: 'Revenue beat rate', value: '62%',              tone: 'sage' },
    ],
    lens: {
      preview: 'What practitioners read in earnings that analysts miss…',
      eyebrow: 'Reading Earnings as a Practitioner',
      body: 'Three things that matter and are not in the headline: <strong>1 — Management language changes.</strong> Specific to vague guidance is the tell. <strong>2 — The avoided question.</strong> What management did not answer directly is more informative than what they did. <strong>3 — Gross margin euphemisms.</strong> "Normalizing" means compressing. "Stabilizing" means still falling. <em>For NFLX tonight: subscriber language and ARPU matter more than EPS.</em>',
      pathways: ['understand', 'ask', 'apply'],
      pathwayLabels: { understand: 'Understand Earnings Analysis →', ask: 'Ask about reading transcripts →', apply: 'Run Signal Read on transcript →' },
    },
  },

  // 4a — Market Breadth (40/60 left)
  breadth: {
    stats: [
      { label: 'Adv / Dec NYSE', value: '2,847', sub: '1,203 declining', tone: 'sage' },
      { label: '52W Highs/Lows', value: '124 / 31', sub: 'Highs leading', tone: 'sage' },
      { label: 'McClellan Osc',  value: '+42',   sub: 'Positive momentum', tone: 'sage' },
      { label: 'Up Volume %',    value: '68%',   sub: 'Pre-market',         tone: 'teal' },
    ],
    bars: [
      { label: 'Above 200MA', pct: 68, value: '68.4%', tone: 'sage' },
      { label: 'Above 50MA',  pct: 62, value: '62.1%', tone: 'sage' },
      { label: 'Above 20MA',  pct: 54, value: '54.3%', tone: 'gold' },
      { label: 'Above 5MA',   pct: 49, value: '49.2%', tone: 'terra' },
    ],
    lens: {
      preview: 'Breadth divergence signal — what it tells you now…',
      eyebrow: 'Reading Breadth Divergence',
      body: '68% above 200MA is healthy. 49% above 5MA tells a different story — the recent rally is being carried by a shrinking number of large-cap names. <strong>When the index makes new highs but breadth deteriorates — that is distribution, not accumulation.</strong> Implications: sector rotation trades are more attractive than broad index plays right now.',
      pathways: ['understand', 'ask', 'apply'],
      pathwayLabels: { understand: 'Understand Market Breadth →', ask: 'Ask about breadth divergence →', apply: 'Apply to Workstation →' },
    },
  },

  // 4b — Sector Rotation (40/60 right, teal-top)
  sectors: {
    rows: [
      { rank: '01', name: 'Technology',       pct: 38, value: '+0.82%', dir: 'up' },
      { rank: '02', name: 'Energy',            pct: 28, value: '+0.61%', dir: 'up' },
      { rank: '03', name: 'Financials',        pct: 20, value: '+0.44%', dir: 'up' },
      { rank: '04', name: 'Industrials',       pct: 10, value: '+0.21%', dir: 'up' },
      { rank: '05', name: 'Communication',     pct: 8,  value: '+0.18%', dir: 'up' },
      { rank: '06', name: 'Materials',         pct: 4,  value: '−0.08%', dir: 'dn' },
      { rank: '07', name: 'Consumer Disc',     pct: 7,  value: '−0.14%', dir: 'dn' },
      { rank: '08', name: 'Healthcare',        pct: 10, value: '−0.22%', dir: 'dn' },
      { rank: '09', name: 'Real Estate',       pct: 18, value: '−0.38%', dir: 'dn' },
      { rank: '10', name: 'Utilities',         pct: 24, value: '−0.52%', dir: 'dn' },
      { rank: '11', name: 'Consumer Staples',  pct: 30, value: '−0.64%', dir: 'dn' },
    ],
    summary: [
      { label: 'Rotation story', value: 'Risk-on · Tech + Energy leading', tone: 'teal' },
      { label: 'Defensive signal', value: 'Utilities + Staples selling', tone: 'terra' },
    ],
    lens: {
      preview: 'What Tech + Energy leading with defensives selling tells you…',
      eyebrow: 'Reading Sector Rotation as a Practitioner',
      body: 'Tech leading with Energy second while defensives sell off is a classic risk-on rotation. <strong>The institutional message: growth expectations are rising.</strong> But watch the conflict: sector rotation implies higher-for-longer rates, while FedWatch says 67% December cut probability. Sector behavior historically leads the options market by 4-6 weeks. <em>If the FOMC minutes are hawkish — Energy and Financials hold, Tech fades.</em>',
      pathways: ['understand', 'ask', 'apply'],
      pathwayLabels: { understand: 'Understand Sector Rotation →', ask: 'Ask about rotation signals →', apply: 'Apply to Workstation →' },
    },
  },

  // 5a — Volatility (50/50 left, terra-top)
  volatility: {
    grid: [
      { label: 'VIX Spot', value: '18.22', tone: 'terra', change: '+1.2%',           dir: 'up' },
      { label: 'VIX9D',    value: '19.44', tone: 'terra', change: 'Event elevated', dir: 'up' },
      { label: 'VIX3M',    value: '17.81', tone: 'mid',   change: 'Below spot',     dir: 'dn' },
      { label: 'VIX6M',    value: '18.04', tone: 'mid',   change: 'Below 9D',       dir: 'dn' },
    ],
    structure: { kind: 'back', label: 'Backwardation · Front > Back · Acute event risk' },
    rows: [
      { label: 'Put/Call Ratio',     value: '1.12 — Slight fear',           tone: 'terra' },
      { label: 'VVIX (vol of vol)',  value: '94.2 — Unstable',              tone: 'gold' },
      { label: '25-delta Skew',      value: '28.4 — Moderate tail demand',  tone: 'gold' },
      { label: 'IV Rank SPY',        value: '68 — Top third of 1yr range',  tone: 'terra' },
    ],
    lens: {
      preview: 'What backwardation means for your positions today…',
      eyebrow: 'Reading Volatility Structure as a Practitioner',
      body: 'VIX9D above VIX spot is backwardation — near-term fear exceeds long-term fear. This happens specifically before events. The market prices an IV spike around FOMC, not sustained elevated vol. <strong>Implication: if you sell options today, you are selling into backwardation — maximally exposed to the event risk.</strong> <em>If you buy options today, buy the back month cheap and sell the front-month spike after the event resolves.</em> High VVIX (94) means VIX itself is unstable — the vol regime can shift quickly after the minutes land.',
      pathways: ['understand', 'ask', 'apply'],
      pathwayLabels: { understand: 'Understand VIX Term Structure →', ask: 'Ask about trading backwardation →', apply: 'Apply to Workstation →' },
    },
  },

  // 5b — Sentiment (50/50 right)
  sentiment: {
    fearGreed: { value: 58, label: 'Greed', leftLabel: 'Extreme Fear', rightLabel: 'Extreme Greed' },
    aaii: [
      { label: 'AAII Bulls / Bears', value: '38.2% / 28.4%',  tone: 'gold' },
      { label: 'Bull-Bear Spread',    value: '+9.8 pts · Moderate', tone: 'gold' },
    ],
    cot: [
      { label: 'Spec net long S&P 500', value: '+$24.4B — Crowded',    tone: 'terra' },
      { label: 'Net spec change WoW',    value: '+$2.1B added longs',   tone: 'gold' },
    ],
    short: [
      { label: 'SPY short interest', value: '4.2% — Low (no squeeze fuel)', tone: 'mid' },
      { label: 'IWM short interest', value: '7.4% — Moderate',              tone: 'gold' },
    ],
    lens: {
      preview: 'Crowded longs + low short interest = fragile positioning…',
      eyebrow: 'Reading Sentiment as a Practitioner',
      body: 'Spec longs at +$24.4B is a crowded trade. Low short interest (4.2% SPY) means minimal short-squeeze fuel — rallies must be driven by new buyers, not shorts covering. <strong>Crowded long + low shorts = fragile positioning.</strong> If FOMC minutes surprise hawkish, the unwind could be violent because everyone is on the same side with no natural buyers stepping in. <em>This is the single biggest tail risk today.</em>',
      pathways: ['understand', 'ask', 'apply'],
      pathwayLabels: { understand: 'Understand COT Positioning →', ask: 'Ask about crowded trades →', apply: 'Apply to Workstation →' },
    },
  },

  // 6a — Rates & Credit (1/3, gold-top)
  rates: {
    yieldCurve: [
      { term: '2Y',  pct: 99, rate: '4.94%', change: '+4bp', dir: 'up' },
      { term: '5Y',  pct: 91, rate: '4.56%', change: '+6bp', dir: 'up' },
      { term: '10Y', pct: 92, rate: '4.61%', change: '+8bp', dir: 'up' },
      { term: '30Y', pct: 95, rate: '4.74%', change: '+5bp', dir: 'up' },
    ],
    rows: [
      { label: '2/10 Spread',     value: '−33bp (inverted)', tone: 'terra' },
      { label: 'Fed Funds Rate',  value: '5.25–5.50%',        tone: 'mid' },
      { label: '30Y Mortgage',    value: '7.12%',             tone: 'gold' },
      { label: 'IG Credit Spread', value: '94bp',             tone: 'sage' },
      { label: 'HY Spread',       value: '312bp',             tone: 'gold' },
    ],
    lens: {
      preview: 'What the inverted yield curve means for traders today…',
      eyebrow: 'Reading the Yield Curve',
      body: 'The 2/10 inversion at −33bp has persisted for over 18 months. For traders: the inversion matters most for sector positioning — financials compress their NIM when the curve inverts, growth stocks get a tailwind when the 10Y is expected to fall. <em>Watch what happens to the curve after FOMC minutes — if the 2Y falls faster than the 10Y, the market is pricing imminent cuts.</em>',
      pathways: ['understand', 'apply'],
      pathwayLabels: { understand: 'Understand Yield Curve →', apply: 'Apply to Workstation →' },
    },
  },

  // 6b — Currency (1/3)
  currency: {
    rows: [
      { label: 'DXY Dollar Index', value: '105.84', tone: 'gold', change: '−0.22%', dir: 'up' },
      { label: 'EUR / USD',         value: '1.0712', tone: 'mid',  change: '+0.18%', dir: 'dn' },
      { label: 'USD / JPY',         value: '154.22', tone: 'terra', change: '−0.31%', dir: 'up' },
      { label: 'GBP / USD',         value: '1.2481', tone: 'mid',  change: '+0.12%', dir: 'dn' },
      { label: 'USD / CNY',         value: '7.2441', tone: 'mid',  change: '+0.08%', dir: 'up' },
    ],
    summary: 'Dollar softening → Multinationals tailwind → Gold and oil bid',
    summaryTone: 'sage',
    lens: {
      preview: 'USD/JPY carry trade signal — what yen strength means…',
      eyebrow: 'Reading Currency as a Practitioner',
      body: 'USD/JPY at 154.22 and falling (yen strengthening) is the carry trade unwind signal to watch. <strong>When the yen strengthens suddenly, traders who borrowed cheaply in yen to buy risk assets must sell those assets to repay.</strong> A forced selling cascade. The BOJ is telegraphing rate hikes — when they act, USD/JPY can drop 3-5% in a session triggering risk-off globally. Watch USD/JPY for sudden moves below 152.',
      pathways: ['understand', 'ask', 'apply'],
      pathwayLabels: { understand: 'Understand Currency and Equities →', ask: 'Ask about the carry trade →', apply: 'Apply to Workstation →' },
    },
  },

  // 6c — Commodities (1/3)
  commodities: {
    rows: [
      { label: 'Gold (XAU/USD)',    value: '$2,384', tone: 'sage',  change: '+0.42%', dir: 'dn' },
      { label: 'WTI Crude Oil',     value: '$82.14', tone: 'terra', change: '+1.24%', dir: 'dn' },
      { label: 'Brent Crude',       value: '$86.22', tone: 'terra', change: '+1.18%', dir: 'dn' },
      { label: 'Copper (Dr. Copper)', value: '$4.48', tone: 'sage', change: '+0.31%', dir: 'dn' },
      { label: 'Natural Gas',       value: '$1.74',  tone: 'mid',   change: '−0.82%', dir: 'up' },
      { label: 'Silver',            value: '$28.42', tone: 'mid',   change: '+0.55%', dir: 'dn' },
    ],
    summary: 'Gold ATH + Oil rising = inflation persisting. Copper positive = growth intact.',
    summaryTone: 'gold',
    lens: {
      preview: 'Gold + Oil + Copper together — the macro message…',
      eyebrow: 'Reading Commodities as a Practitioner',
      body: 'Gold at all-time highs while oil rises and copper holds is the stagflation trio. <strong>The Fed\'s dilemma: copper says don\'t slow the economy, gold and oil say inflation is not beaten.</strong> This is why the market is uncertain about the next Fed move. <em>The commodity message today is more bearish for rate cuts than FedWatch implies — watch FOMC minutes for commodity price language.</em>',
      pathways: ['understand', 'ask', 'apply'],
      pathwayLabels: { understand: 'Understand Commodities as Macro →', ask: 'Ask about stagflation trading →', apply: 'Apply to Workstation →' },
    },
  },

  // 7 — Economic Scoreboard (full-width, 4x3 grid)
  scoreboard: {
    cells: [
      { label: 'CPI YoY',           value: '3.5%',     tone: 'hot',     trend: { tone: 'hot',     label: 'Re-accelerating' },         ctx: 'Target 2.0%. Rose from 3.2% prior.' },
      { label: 'PCE YoY (Fed pref)', value: '2.7%',     tone: 'hot',     trend: { tone: 'hot',     label: 'Above target' },             ctx: 'Not falling fast enough.' },
      { label: 'Core PCE YoY',      value: '2.8%',     tone: 'hot',     trend: { tone: 'neutral', label: 'Sticky services inflation' }, ctx: 'Services not cooling.' },
      { label: 'Non-Farm Payrolls', value: '+303K',    tone: 'cool',    trend: { tone: 'cool',    label: 'Strong' },                   ctx: 'Above 180K estimate.' },
      { label: 'Unemployment',      value: '3.8%',     tone: 'cool',    trend: { tone: 'neutral', label: 'Near full employment' },     ctx: 'Sahm rule not triggered.' },
      { label: 'GDP Q4 2025',       value: '+3.4%',    tone: 'cool',    trend: { tone: 'cool',    label: 'Above trend' },              ctx: 'Above 2.0% trend.' },
      { label: 'ISM Manufacturing', value: '50.3',     tone: 'neutral', trend: { tone: 'neutral', label: 'Barely expanding' },         ctx: 'Above 50 = expansion.' },
      { label: 'ISM Services',      value: '51.4',     tone: 'cool',    trend: { tone: 'cool',    label: 'Expanding' },                ctx: '70% of economy. Holding.' },
      { label: 'Atlanta GDPNow Q1', value: '+2.8%',    tone: 'cool',    trend: { tone: 'cool',    label: 'Real-time estimate' },       ctx: 'Above trend, running.' },
      { label: 'Retail Sales MoM',  value: '+0.7%',    tone: 'cool',    trend: { tone: 'cool',    label: 'Consumer resilient' },       ctx: 'Spending holding up.' },
      { label: 'Jobless Claims',    value: '222K prev', tone: 'neutral', trend: { tone: 'neutral', label: 'Today exp 215K' },           ctx: 'Today at 7:30 AM.' },
      { label: 'Fed Policy',        value: '5.25–5.50%', tone: 'hot',    trend: { tone: 'hot',     label: 'Holding steady' },           ctx: 'Data-dependent. No cuts.' },
    ],
    lens: {
      preview: 'Strong growth + sticky inflation — what it means for the market\'s valuation…',
      eyebrow: 'Reading the Macro Scoreboard as a Practitioner',
      body: 'The scoreboard tells one story: <strong>the economy is strong, inflation is not beaten, the Fed cannot cut yet.</strong> This is the higher-for-longer regime. Strong growth is positive for earnings — positive for stocks. But CPI re-accelerating (3.5% from 3.2%) means the multiple expansion trade (stocks up because rates fall) is losing its logic. <em>What replaces multiple expansion: actual earnings growth. The market has to grow into its valuation through results — not rate cut hope.</em> That means earnings quality matters more now than it did in 2023-2024.',
      pathways: ['understand', 'ask', 'apply'],
      pathwayLabels: { understand: 'Understand Macro and Markets →', ask: 'Ask about higher-for-longer impact →', apply: 'Apply to Workstation →' },
    },
  },

  // 8a — Intelligence Feed (50/50 left)
  feed: {
    items: [
      {
        id: 'f1',
        dot: 'terra',
        source: 'CME FedWatch · Rate Probability',
        time: '6:42 AM',
        body: 'December cut probability <em class="teal">67.4%</em> after PCE data. Sector rotation and FedWatch are sending conflicting signals — <strong>sector behavior historically leads by 4-6 weeks.</strong>',
        lens: {
          body: 'Sector behavior (Energy and Financials outperforming, Utilities underperforming) is more consistent with no near-term cut than the FedWatch probability implies. <strong>The fed funds futures market is pricing hope. The sector behavior is pricing reality.</strong>',
          pathways: ['understand', 'apply'],
          pathwayLabels: { understand: 'Understand FedWatch →', apply: 'Apply to Workstation →' },
        },
      },
      {
        id: 'f2',
        dot: 'teal',
        source: 'Options Flow Monitor · Unusual Activity',
        time: '6:38 AM',
        body: 'NVDA — heavy call buying at <em class="teal">$900 strike</em> expiring next Friday. Volume 3.4× open interest. New positioning. <strong>Market maker delta hedging creates mechanical buying near $895.</strong>',
        lens: {
          body: '3.4× OI = new institutional positioning, not rolling. As NVDA approaches $895, market makers who sold these calls must buy increasing shares to stay delta-neutral. This creates <strong>mechanical demand — price can accelerate past $895 not because of new buyers, but because of hedging flows.</strong>',
          pathways: ['understand', 'ask', 'apply'],
          pathwayLabels: { understand: 'Understand Options Flow →', ask: 'Ask about delta hedging →', apply: 'Apply to Workstation →' },
        },
      },
      {
        id: 'f3',
        dot: 'gold',
        source: 'FRED · Treasury Yield',
        time: '6:15 AM',
        body: '<strong>10Y yield +8bp overnight</strong> — third consecutive daily rise. <em class="gold">Growth stocks sensitive to rate moves — watch tech first 30 minutes for rate impact confirmation.</em>',
        lens: {
          body: 'Three consecutive daily rises in 10Y is a trend, not noise. Tech stocks trade at high forward multiples — most sensitive to rate rises. If 10Y moves above 4.70% this week, <strong>expect tech multiple compression to accelerate.</strong> Nasdaq futures at +0.41% pre-market suggests the market has not fully priced in the 3-day yield trend yet.',
          pathways: ['understand', 'apply'],
          pathwayLabels: { understand: 'Understand Rates + Growth →', apply: 'Apply to Workstation →' },
        },
      },
      {
        id: 'f4',
        dot: 'purple',
        source: 'Your Signal Matrix · Pattern Detected',
        time: '6:00 AM · Personal signal',
        body: '<strong>Squeeze study produced above-average outcomes in 3 of 3 sessions</strong> following FOMC releases where VIX was above 18. Current VIX: 18.22. Your historical edge conditions are present today.',
        lens: null, // personal signals don't have a separate lens; the body IS the insight
      },
    ],
  },

  // 8b — Community Pulse (50/50 right)
  community: {
    activeCount: 847,
    rows: [
      [
        { kind: 'disc', text: '<strong>"How are you positioning into FOMC today?"</strong> Split: 58% waiting, 42% pre-positioned on IV spike.', meta: '34 practitioners · 2h ago' },
        { kind: 'art',  text: '<strong>"Reading Verb Tense Shifts in Fed Language"</strong> — The 4 qualifier changes that preceded the last 3 rate decisions.', meta: 'Published today · 12 min read' },
        { kind: 'qa',   text: '<strong>"Squeeze on a FOMC day — take it?"</strong> Expert consensus: yes, size down, wait for 10-min post-release confirmation.', meta: '3 expert answers · 1h ago' },
      ],
      [
        { kind: 'disc', text: '<strong>"WTI at $82 — energy worth adding before close?"</strong> 18 practitioners debating XOM, CVX, COP implications.', meta: '18 practitioners · 3h ago' },
        { kind: 'qa',   text: '<strong>"NVDA call flow — institutional or retail?"</strong> Level 4 answer: 3.4× OI ratio points institutional. Retail is typically 1-2×.', meta: '1 expert answer · 45m ago' },
        { kind: 'art',  text: '<strong>"Triple Witching Tomorrow — What Changes"</strong> How to adjust Conviction Engine signals in the final 90 minutes.', meta: 'Published yesterday · 8 min' },
      ],
    ],
  },
};
